/**
 * What every section of the Team settings sheet reads and writes: the Team as
 * it was read, the drafts the sheet holds, and the acts that land at once
 * (Turn off, Remove, Propose now), which re-read the Team when they do.
 */
import type { Job, JobKindWire, TeamAgentWire } from "@yagni/shared";

import type { RepoScope } from "../../desks/RepoScopeLine.js";
import type { CheckDrafts } from "../ChecksRoster.js";
import type { TeamPageState } from "../useTeamPage.js";
import type { LineDraft, TeamDraft } from "./settingsDraft.js";

export interface Person {
  userId: string;
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
}

export interface SettingsCtx {
  deskId: string;
  page: TeamPageState;
  /**
   * Nothing can be edited: the viewer cannot manage this Team, or it is
   * archived. Whoever can manage it changes every field, its name included
   * (`useCanManageTeam`: the Team's owner, a workspace owner or admin, or the
   * operator in God Mode with Act on).
   */
  readOnly: boolean;
  /** A save or an act is in flight. */
  busy: boolean;
  isAdmin: boolean;
  viewerId: string | null;
  team: TeamDraft;
  setTeam: (update: (draft: TeamDraft) => TeamDraft) => void;
  /** The draft of a line: the sheet's own once touched, else the line as read. */
  line: (job: Job) => LineDraft;
  setLine: (job: Job, update: (draft: LineDraft) => LineDraft) => void;
  checkDrafts: CheckDrafts;
  setCheckDrafts: (drafts: CheckDrafts) => void;
  /** Everyone in the workspace, then anyone on the Team the list missed. */
  people: Person[];
  /** The workspace's Slack channels, for Posts to. */
  slackChannels: Array<{ id: string; name: string }>;
  /** The workspace's repositories, for Works on. */
  availableRepos: string[];
  /** Whose GitHub installation those come from, for the line under the search. */
  repoScope?: RepoScope | null;
  /** The workspace's declared systems, for System; empty when the viewer may not read them. */
  systems: Array<{ id: string; name: string }>;
  /** Run an act that lands at once, say what happened, and re-read the Team. */
  act: (run: () => Promise<unknown>, done?: string) => Promise<boolean>;
  /** Scroll the sheet to a section. */
  goto: (section: string) => void;
  /** The match count the filter's preview last answered, for the Team card's Reads echo. */
  matches: number | null;
  setMatches: (total: number | null) => void;
  /** An unsaved edit other than the backlog filter, which leaving the sheet would lose. */
  editsBesideFilter?: boolean;
  agentFor: (craft: string) => TeamAgentWire | null;
  kindOf: (kind: string) => JobKindWire | null;
}

/** Who a supervised line names when it does not name anybody yet: the Team's owner, else its first member. */
export function defaultSupervisor(ctx: SettingsCtx): string | null {
  return ctx.page.team?.publisherUserId ?? ctx.page.members[0]?.userId ?? ctx.viewerId;
}
